/**
 * Workspace Configuration Presets for Multi-Industry CRM
 * 
 * Provides default terminology, fields, and pipeline stages per business profile:
 * - Trading & Distribution (Wholesale, Commodity, Import/Export, Goods)
 * - Professional Services & Tech (Agencies, Consulting, IT)
 * - Real Estate & Property
 * - Manufacturing & Industrial
 * - Healthcare & Clinics
 * - Custom / General B2B
 */

export interface WorkspaceFieldConfig {
  key: string;
  label: string;
  type: "text" | "number" | "select" | "textarea";
  visible: boolean;
  required?: boolean;
  placeholder?: string;
  options?: string[];
}

export interface WorkspacePipelineStage {
  id: string;
  name: string;
  color: string;
  bg?: string;
  border?: string;
  position: number;
  isWon?: boolean;
  isLost?: boolean;
}

export interface WorkspaceConfig {
  userId: number;
  businessProfile: "trading" | "services" | "real_estate" | "manufacturing" | "healthcare" | "custom" | string;
  fields: WorkspaceFieldConfig[];
  pipelineStages: WorkspacePipelineStage[];
  importMappings?: Record<string, string>;
  updatedAt?: string;
}

export const WORKSPACE_PRESETS: Record<string, {
  name: string;
  icon: string;
  description: string;
  fields: WorkspaceFieldConfig[];
  pipelineStages: WorkspacePipelineStage[];
}> = {
  trading: {
    name: "Trading & Distribution",
    icon: "📦",
    description: "For wholesale traders, commodity brokers, distributors, and import/export enterprises.",
    fields: [
      { key: "name", label: "Buyer / Contact Person", type: "text", visible: true, required: true, placeholder: "e.g. Rajesh Singhania" },
      { key: "company_name", label: "Trading Firm / Company", type: "text", visible: true, required: true, placeholder: "e.g. Singhania Metals & Tubes Pvt Ltd" },
      { key: "phone", label: "WhatsApp / Contact Phone", type: "text", visible: true, required: true, placeholder: "+91 98765 43210" },
      { key: "email", label: "Official Email", type: "text", visible: true, required: false, placeholder: "purchase@singhaniametals.com" },
      { key: "service_type", label: "Product / Commodity", type: "text", visible: true, required: true, placeholder: "e.g. Hot Rolled Steel Coils / Agro Rice" },
      { key: "quantity", label: "Quantity / Volume", type: "number", visible: true, required: false, placeholder: "500" },
      { key: "unit", label: "Unit of Measurement", type: "text", visible: true, required: false, placeholder: "Metric Tonnes (MT) / Units / Containers" },
      { key: "estimated_deal_value", label: "Trade / Order Value", type: "number", visible: true, required: true, placeholder: "750000" },
      { key: "country_or_city", label: "Delivery Port / Destination", type: "text", visible: true, required: false, placeholder: "e.g. Nhava Sheva Port, Mumbai" },
      { key: "priority", label: "Inquiry Priority", type: "select", visible: true, required: true, placeholder: "HOT" },
      { key: "notes", label: "Incoterms & Payment Terms", type: "textarea", visible: true, required: false, placeholder: "e.g. CIF Nhava Sheva, 100% LC at Sight, Inspection by SGS" },
    ],
    pipelineStages: [
      { id: "Inquiry / RFQ", name: "Inquiry / RFQ", color: "text-blue-500", bg: "bg-blue-500/10", border: "border-blue-500/20", position: 1 },
      { id: "Quotation Sent", name: "Quotation Sent", color: "text-violet-500", bg: "bg-violet-500/10", border: "border-violet-500/20", position: 2 },
      { id: "Sample / Specs", name: "Sample / Specs", color: "text-amber-500", bg: "bg-amber-500/10", border: "border-amber-500/20", position: 3 },
      { id: "Negotiation / PO", name: "Negotiation / PO", color: "text-orange-500", bg: "bg-orange-500/10", border: "border-orange-500/20", position: 4 },
      { id: "Order Dispatched / Won", name: "Order Dispatched / Won", color: "text-emerald-500", bg: "bg-emerald-500/10", border: "border-emerald-500/20", position: 5, isWon: true },
      { id: "Cancelled / Lost", name: "Cancelled / Lost", color: "text-slate-400", bg: "bg-slate-500/10", border: "border-slate-500/20", position: 6, isLost: true },
    ],
  },

  services: {
    name: "Professional Services & Tech",
    icon: "💼",
    description: "For agencies, consulting firms, IT services, and B2B solution providers.",
    fields: [
      { key: "name", label: "Client / Prospect Name", type: "text", visible: true, required: true, placeholder: "e.g. John Miller" },
      { key: "company_name", label: "Client Enterprise / Brand", type: "text", visible: true, required: true, placeholder: "e.g. Acme Corporation" },
      { key: "phone", label: "WhatsApp Phone", type: "text", visible: true, required: true, placeholder: "+91 98765 43210" },
      { key: "email", label: "Email Address", type: "text", visible: true, required: false, placeholder: "john@acme.com" },
      { key: "service_type", label: "Service / Solution Scope", type: "text", visible: true, required: true, placeholder: "e.g. Custom Software / Cloud ERP" },
      { key: "estimated_deal_value", label: "Contract / Deal Value", type: "number", visible: true, required: true, placeholder: "65000" },
      { key: "country_or_city", label: "City / Market Location", type: "text", visible: true, required: false, placeholder: "Bangalore" },
      { key: "priority", label: "Deal Priority", type: "select", visible: true, required: true, placeholder: "HOT" },
      { key: "notes", label: "Project Scope & Notes", type: "textarea", visible: true, required: false, placeholder: "Key deliverables and timeline requirement" },
    ],
    pipelineStages: [
      { id: "New Lead", name: "New Lead", color: "text-blue-500", bg: "bg-blue-500/10", border: "border-blue-500/20", position: 1 },
      { id: "Discovery Call", name: "Discovery Call", color: "text-violet-500", bg: "bg-violet-500/10", border: "border-violet-500/20", position: 2 },
      { id: "Proposal Sent", name: "Proposal Sent", color: "text-amber-500", bg: "bg-amber-500/10", border: "border-amber-500/20", position: 3 },
      { id: "Negotiation", name: "Negotiation", color: "text-orange-500", bg: "bg-orange-500/10", border: "border-orange-500/20", position: 4 },
      { id: "Closed Won", name: "Closed Won", color: "text-emerald-500", bg: "bg-emerald-500/10", border: "border-emerald-500/20", position: 5, isWon: true },
      { id: "Closed Lost", name: "Closed Lost", color: "text-slate-400", bg: "bg-slate-500/10", border: "border-slate-500/20", position: 6, isLost: true },
    ],
  },

  real_estate: {
    name: "Real Estate & Brokerage",
    icon: "🏢",
    description: "For builders, property brokerages, and real estate developer sales desks.",
    fields: [
      { key: "name", label: "Buyer / Investor Name", type: "text", visible: true, required: true, placeholder: "e.g. Vikram Malhotra" },
      { key: "company_name", label: "Investor Firm / Co-Buyer", type: "text", visible: false, required: false, placeholder: "Malhotra Holdings" },
      { key: "phone", label: "WhatsApp Phone", type: "text", visible: true, required: true, placeholder: "+971 50 123 4567" },
      { key: "email", label: "Email Address", type: "text", visible: true, required: false, placeholder: "vikram@example.com" },
      { key: "service_type", label: "Property / Unit Type", type: "text", visible: true, required: true, placeholder: "e.g. 3BHK Luxury High-Rise / Waterfront Villa" },
      { key: "estimated_deal_value", label: "Property Budget / Value", type: "number", visible: true, required: true, placeholder: "18500000" },
      { key: "country_or_city", label: "Target Micro-Market / City", type: "text", visible: true, required: false, placeholder: "Dubai Marina / Whitefield" },
      { key: "priority", label: "Buyer Intent", type: "select", visible: true, required: true, placeholder: "HOT" },
      { key: "notes", label: "Financing & Site Visit Notes", type: "textarea", visible: true, required: false, placeholder: "e.g. Pre-approved home loan, weekend site visit requested" },
    ],
    pipelineStages: [
      { id: "New Inquiry", name: "New Inquiry", color: "text-blue-500", bg: "bg-blue-500/10", border: "border-blue-500/20", position: 1 },
      { id: "Site Visit / Tour", name: "Site Visit / Tour", color: "text-violet-500", bg: "bg-violet-500/10", border: "border-violet-500/20", position: 2 },
      { id: "Negotiation", name: "Negotiation", color: "text-amber-500", bg: "bg-amber-500/10", border: "border-amber-500/20", position: 3 },
      { id: "Token / Booking", name: "Token / Booking", color: "text-orange-500", bg: "bg-orange-500/10", border: "border-orange-500/20", position: 4 },
      { id: "Agreement Executed", name: "Agreement Executed", color: "text-emerald-500", bg: "bg-emerald-500/10", border: "border-emerald-500/20", position: 5, isWon: true },
      { id: "Drop / Lost", name: "Drop / Lost", color: "text-slate-400", bg: "bg-slate-500/10", border: "border-slate-500/20", position: 6, isLost: true },
    ],
  },

  manufacturing: {
    name: "Manufacturing & Industrial",
    icon: "🏭",
    description: "For manufacturers, fabrication units, and industrial equipment makers.",
    fields: [
      { key: "name", label: "Procurement Officer", type: "text", visible: true, required: true, placeholder: "e.g. Sunil Deshmukh" },
      { key: "company_name", label: "Industrial Enterprise", type: "text", visible: true, required: true, placeholder: "e.g. Tata Motors Tier-1 Vendor" },
      { key: "phone", label: "Phone Number", type: "text", visible: true, required: true, placeholder: "+91 98765 43210" },
      { key: "email", label: "Email Address", type: "text", visible: true, required: false, placeholder: "procurement@client.com" },
      { key: "service_type", label: "Part / Component / Assembly", type: "text", visible: true, required: true, placeholder: "e.g. CNC Milled Aluminium Enclosures" },
      { key: "quantity", label: "Batch Quantity", type: "number", visible: true, required: false, placeholder: "10000" },
      { key: "unit", label: "Unit (Pieces/Sets)", type: "text", visible: true, required: false, placeholder: "Pcs" },
      { key: "estimated_deal_value", label: "PO / Contract Value", type: "number", visible: true, required: true, placeholder: "1250000" },
      { key: "country_or_city", label: "Plant / Delivery Location", type: "text", visible: true, required: false, placeholder: "Pune Chakan MIDC" },
      { key: "priority", label: "Urgency", type: "select", visible: true, required: true, placeholder: "HOT" },
      { key: "notes", label: "Technical Specs & Tolerance", type: "textarea", visible: true, required: false, placeholder: "Drawing Rev 3.2, ±0.02mm tolerance" },
    ],
    pipelineStages: [
      { id: "RFQ Received", name: "RFQ Received", color: "text-blue-500", bg: "bg-blue-500/10", border: "border-blue-500/20", position: 1 },
      { id: "Feasibility Review", name: "Feasibility Review", color: "text-violet-500", bg: "bg-violet-500/10", border: "border-violet-500/20", position: 2 },
      { id: "Quote & Prototype", name: "Quote & Prototype", color: "text-amber-500", bg: "bg-amber-500/10", border: "border-amber-500/20", position: 3 },
      { id: "Commercial Terms", name: "Commercial Terms", color: "text-orange-500", bg: "bg-orange-500/10", border: "border-orange-500/20", position: 4 },
      { id: "PO Issued / In Prod", name: "PO Issued / In Prod", color: "text-emerald-500", bg: "bg-emerald-500/10", border: "border-emerald-500/20", position: 5, isWon: true },
      { id: "No Bid / Inactive", name: "No Bid / Inactive", color: "text-slate-400", bg: "bg-slate-500/10", border: "border-slate-500/20", position: 6, isLost: true },
    ],
  },

  healthcare: {
    name: "Healthcare & Clinics",
    icon: "🏥",
    description: "For medical clinics, dental practices, diagnostic labs, and healthcare centers.",
    fields: [
      { key: "name", label: "Patient / Client Name", type: "text", visible: true, required: true, placeholder: "e.g. Ananya Rao" },
      { key: "company_name", label: "Corporate / Insurer", type: "text", visible: false, required: false, placeholder: "Star Health / Self-Pay" },
      { key: "phone", label: "Contact Phone / WhatsApp", type: "text", visible: true, required: true, placeholder: "+91 98765 43210" },
      { key: "email", label: "Email Address", type: "text", visible: true, required: false, placeholder: "patient@example.com" },
      { key: "service_type", label: "Treatment / Consultation", type: "text", visible: true, required: true, placeholder: "e.g. Dental Implant / Full Body Checkup" },
      { key: "estimated_deal_value", label: "Estimated Fee / Procedure", type: "number", visible: true, required: true, placeholder: "25000" },
      { key: "country_or_city", label: "Location / Branch", type: "text", visible: true, required: false, placeholder: "Indiranagar Clinic" },
      { key: "priority", label: "Urgency", type: "select", visible: true, required: true, placeholder: "HOT" },
      { key: "notes", label: "Clinical Notes / History", type: "textarea", visible: true, required: false, placeholder: "Referred by Dr. Verma, appointment confirmed for Thursday" },
    ],
    pipelineStages: [
      { id: "Inquiry / Consultation", name: "Inquiry / Consultation", color: "text-blue-500", bg: "bg-blue-500/10", border: "border-blue-500/20", position: 1 },
      { id: "Assessment Scheduled", name: "Assessment Scheduled", color: "text-violet-500", bg: "bg-violet-500/10", border: "border-violet-500/20", position: 2 },
      { id: "Treatment Plan Shared", name: "Treatment Plan Shared", color: "text-amber-500", bg: "bg-amber-500/10", border: "border-amber-500/20", position: 3 },
      { id: "Under Care / Active", name: "Under Care / Active", color: "text-orange-500", bg: "bg-orange-500/10", border: "border-orange-500/20", position: 4 },
      { id: "Completed / Discharged", name: "Completed / Discharged", color: "text-emerald-500", bg: "bg-emerald-500/10", border: "border-emerald-500/20", position: 5, isWon: true },
      { id: "Cancelled / No Show", name: "Cancelled / No Show", color: "text-slate-400", bg: "bg-slate-500/10", border: "border-slate-500/20", position: 6, isLost: true },
    ],
  },

  logistics: {
    name: "Logistics, Freight & Warehousing",
    icon: "🚢",
    description: "For freight forwarders, cargo agents, shipping lines, and 3PL warehousing companies.",
    fields: [
      { key: "name", label: "Consignee / Contact Person", type: "text", visible: true, required: true, placeholder: "e.g. Ramesh Kulkarni" },
      { key: "company_name", label: "Shipper / Logistics Client", type: "text", visible: true, required: true, placeholder: "e.g. BlueDart Partner / Global Importers" },
      { key: "phone", label: "WhatsApp Phone", type: "text", visible: true, required: true, placeholder: "+91 98765 43210" },
      { key: "email", label: "Work Email", type: "text", visible: true, required: false, placeholder: "shipping@importers.com" },
      { key: "service_type", label: "Cargo / Freight Type", type: "text", visible: true, required: true, placeholder: "e.g. FCL 40ft Container / Air Cargo" },
      { key: "quantity", label: "Tonnage / Container Count", type: "number", visible: true, required: false, placeholder: "4" },
      { key: "unit", label: "Unit (TEU / MT / Pallets)", type: "text", visible: true, required: false, placeholder: "Containers (TEU)" },
      { key: "estimated_deal_value", label: "Freight & Clearance Value", type: "number", visible: true, required: true, placeholder: "320000" },
      { key: "country_or_city", label: "Origin & Destination Port", type: "text", visible: true, required: false, placeholder: "Jebel Ali to Nhava Sheva" },
      { key: "priority", label: "Urgency", type: "select", visible: true, required: true, placeholder: "HOT" },
      { key: "notes", label: "Shipping Terms & Bill of Lading", type: "textarea", visible: true, required: false, placeholder: "Customs clearance needed, temperature controlled" },
    ],
    pipelineStages: [
      { id: "Freight RFQ", name: "Freight RFQ", color: "text-blue-500", bg: "bg-blue-500/10", border: "border-blue-500/20", position: 1 },
      { id: "Rate Quote Sent", name: "Rate Quote Sent", color: "text-violet-500", bg: "bg-violet-500/10", border: "border-violet-500/20", position: 2 },
      { id: "Booking Confirmed", name: "Booking Confirmed", color: "text-amber-500", bg: "bg-amber-500/10", border: "border-amber-500/20", position: 3 },
      { id: "In Transit / Customs", name: "In Transit / Customs", color: "text-orange-500", bg: "bg-orange-500/10", border: "border-orange-500/20", position: 4 },
      { id: "Delivered & Billed", name: "Delivered & Billed", color: "text-emerald-500", bg: "bg-emerald-500/10", border: "border-emerald-500/20", position: 5, isWon: true },
      { id: "Cancelled / Lost", name: "Cancelled / Lost", color: "text-slate-400", bg: "bg-slate-500/10", border: "border-slate-500/20", position: 6, isLost: true },
    ],
  },

  fitness: {
    name: "Fitness Center & Gym",
    icon: "🏋️",
    description: "For fitness studios, CrossFit boxes, personal training gyms, and wellness clubs.",
    fields: [
      { key: "name", label: "Member / Prospect Name", type: "text", visible: true, required: true, placeholder: "e.g. Rohan Verma" },
      { key: "company_name", label: "Corporate Sponsor (If Any)", type: "text", visible: false, required: false, placeholder: "e.g. Infosys Wellness Program" },
      { key: "phone", label: "WhatsApp Phone", type: "text", visible: true, required: true, placeholder: "+91 98765 43210" },
      { key: "email", label: "Email Address", type: "text", visible: true, required: false, placeholder: "rohan@example.com" },
      { key: "service_type", label: "Membership / Training Plan", type: "text", visible: true, required: true, placeholder: "e.g. Annual Premium + Personal Training" },
      { key: "estimated_deal_value", label: "Plan Value", type: "number", visible: true, required: true, placeholder: "28000" },
      { key: "country_or_city", label: "Preferred Branch", type: "text", visible: true, required: false, placeholder: "Indiranagar Club" },
      { key: "priority", label: "Fitness Goal Urgency", type: "select", visible: true, required: true, placeholder: "HOT" },
      { key: "notes", label: "Health & Training Goals", type: "textarea", visible: true, required: false, placeholder: "Weight loss goal, evening trial slot requested" },
    ],
    pipelineStages: [
      { id: "New Lead", name: "New Lead", color: "text-blue-500", bg: "bg-blue-500/10", border: "border-blue-500/20", position: 1 },
      { id: "Trial Pass Claimed", name: "Trial Pass Claimed", color: "text-violet-500", bg: "bg-violet-500/10", border: "border-violet-500/20", position: 2 },
      { id: "Gym Tour / Workout", name: "Gym Tour / Workout", color: "text-amber-500", bg: "bg-amber-500/10", border: "border-amber-500/20", position: 3 },
      { id: "Plan Negotiation", name: "Plan Negotiation", color: "text-orange-500", bg: "bg-orange-500/10", border: "border-orange-500/20", position: 4 },
      { id: "Member Enrolled", name: "Member Enrolled", color: "text-emerald-500", bg: "bg-emerald-500/10", border: "border-emerald-500/20", position: 5, isWon: true },
      { id: "No Show / Inactive", name: "No Show / Inactive", color: "text-slate-400", bg: "bg-slate-500/10", border: "border-slate-500/20", position: 6, isLost: true },
    ],
  },

  hospitality: {
    name: "Restaurant & Banquet Hospitality",
    icon: "🍽️",
    description: "For restaurants, cloud kitchens, banquet halls, and event catering operators.",
    fields: [
      { key: "name", label: "Guest / Host Name", type: "text", visible: true, required: true, placeholder: "e.g. Priya Nambiar" },
      { key: "company_name", label: "Corporate / Event Organiser", type: "text", visible: false, required: false, placeholder: "Wipro Corporate Dinner" },
      { key: "phone", label: "WhatsApp Phone", type: "text", visible: true, required: true, placeholder: "+91 98765 43210" },
      { key: "email", label: "Email Address", type: "text", visible: true, required: false, placeholder: "priya@company.com" },
      { key: "service_type", label: "Event / Reservation Type", type: "text", visible: true, required: true, placeholder: "e.g. Banquet Party / Table Booking" },
      { key: "quantity", label: "Expected Guest Count", type: "number", visible: true, required: false, placeholder: "80" },
      { key: "unit", label: "Unit (Guests / Covers)", type: "text", visible: true, required: false, placeholder: "Guests" },
      { key: "estimated_deal_value", label: "Estimated Spend / Budget", type: "number", visible: true, required: true, placeholder: "95000" },
      { key: "country_or_city", label: "Venue Location / Branch", type: "text", visible: true, required: false, placeholder: "Main Dining Hall, Koramangala" },
      { key: "priority", label: "Booking Urgency", type: "select", visible: true, required: true, placeholder: "HOT" },
      { key: "notes", label: "Menu Preferences & Event Date", type: "textarea", visible: true, required: false, placeholder: "Buffet setup, live bar counter, 28th October" },
    ],
    pipelineStages: [
      { id: "Reservation Inquiry", name: "Reservation Inquiry", color: "text-blue-500", bg: "bg-blue-500/10", border: "border-blue-500/20", position: 1 },
      { id: "Menu & Quote Shared", name: "Menu & Quote Shared", color: "text-violet-500", bg: "bg-violet-500/10", border: "border-violet-500/20", position: 2 },
      { id: "Tasting / Walkthrough", name: "Tasting / Walkthrough", color: "text-amber-500", bg: "bg-amber-500/10", border: "border-amber-500/20", position: 3 },
      { id: "Deposit Paid / Confirmed", name: "Deposit Paid / Confirmed", color: "text-orange-500", bg: "bg-orange-500/10", border: "border-orange-500/20", position: 4 },
      { id: "Event Hosted / Won", name: "Event Hosted / Won", color: "text-emerald-500", bg: "bg-emerald-500/10", border: "border-emerald-500/20", position: 5, isWon: true },
      { id: "Cancelled", name: "Cancelled", color: "text-slate-400", bg: "bg-slate-500/10", border: "border-slate-500/20", position: 6, isLost: true },
    ],
  },

  legal: {
    name: "Legal, CA & Advisory",
    icon: "⚖️",
    description: "For law firms, chartered accountants, tax advisory, and compliance practices.",
    fields: [
      { key: "name", label: "Client / Representative", type: "text", visible: true, required: true, placeholder: "e.g. Arvind Swaminathan" },
      { key: "company_name", label: "Corporate Entity / Client Firm", type: "text", visible: true, required: false, placeholder: "Swaminathan Ventures LLP" },
      { key: "phone", label: "WhatsApp Phone", type: "text", visible: true, required: true, placeholder: "+91 98765 43210" },
      { key: "email", label: "Email Address", type: "text", visible: true, required: false, placeholder: "arvind@ventures.com" },
      { key: "service_type", label: "Legal / Tax Scope", type: "text", visible: true, required: true, placeholder: "e.g. M&A Due Diligence / GST Dispute" },
      { key: "estimated_deal_value", label: "Retainer / Advisory Fee", type: "number", visible: true, required: true, placeholder: "150000" },
      { key: "country_or_city", label: "Jurisdiction / City", type: "text", visible: true, required: false, placeholder: "High Court of Karnataka, Bangalore" },
      { key: "priority", label: "Priority", type: "select", visible: true, required: true, placeholder: "HOT" },
      { key: "notes", label: "Matter Notes & Deadlines", type: "textarea", visible: true, required: false, placeholder: "Hearing scheduled in 3 weeks, documents shared on drive" },
    ],
    pipelineStages: [
      { id: "Case Intake", name: "Case Intake", color: "text-blue-500", bg: "bg-blue-500/10", border: "border-blue-500/20", position: 1 },
      { id: "Conflict Check & Review", name: "Conflict Check & Review", color: "text-violet-500", bg: "bg-violet-500/10", border: "border-violet-500/20", position: 2 },
      { id: "Engagement Letter Sent", name: "Engagement Letter Sent", color: "text-amber-500", bg: "bg-amber-500/10", border: "border-amber-500/20", position: 3 },
      { id: "Matter Active", name: "Matter Active", color: "text-orange-500", bg: "bg-orange-500/10", border: "border-orange-500/20", position: 4 },
      { id: "Resolved & Billed", name: "Resolved & Billed", color: "text-emerald-500", bg: "bg-emerald-500/10", border: "border-emerald-500/20", position: 5, isWon: true },
      { id: "Declined / Closed", name: "Declined / Closed", color: "text-slate-400", bg: "bg-slate-500/10", border: "border-slate-500/20", position: 6, isLost: true },
    ],
  },

  education: {
    name: "Education, Institutes & EdTech",
    icon: "🎓",
    description: "For coaching academies, private universities, professional training centers, and schools.",
    fields: [
      { key: "name", label: "Student / Candidate Name", type: "text", visible: true, required: true, placeholder: "e.g. Siddharth Menon" },
      { key: "company_name", label: "Parent / Sponsor Name", type: "text", visible: false, required: false, placeholder: "K. Menon" },
      { key: "phone", label: "WhatsApp Phone", type: "text", visible: true, required: true, placeholder: "+91 98765 43210" },
      { key: "email", label: "Email Address", type: "text", visible: true, required: false, placeholder: "siddharth@example.com" },
      { key: "service_type", label: "Program / Course", type: "text", visible: true, required: true, placeholder: "e.g. Full Stack AI Engineering Diploma" },
      { key: "estimated_deal_value", label: "Tuition / Course Fee", type: "number", visible: true, required: true, placeholder: "85000" },
      { key: "country_or_city", label: "City / Campus", type: "text", visible: true, required: false, placeholder: "Bangalore Campus" },
      { key: "priority", label: "Admission Priority", type: "select", visible: true, required: true, placeholder: "HOT" },
      { key: "notes", label: "Academic Background & Intake", type: "textarea", visible: true, required: false, placeholder: "B.Tech final year, joining November batch" },
    ],
    pipelineStages: [
      { id: "Course Inquiry", name: "Course Inquiry", color: "text-blue-500", bg: "bg-blue-500/10", border: "border-blue-500/20", position: 1 },
      { id: "Counseling Call", name: "Counseling Call", color: "text-violet-500", bg: "bg-violet-500/10", border: "border-violet-500/20", position: 2 },
      { id: "Demo Class / Campus Visit", name: "Demo Class / Campus Visit", color: "text-amber-500", bg: "bg-amber-500/10", border: "border-amber-500/20", position: 3 },
      { id: "Application Submitted", name: "Application Submitted", color: "text-orange-500", bg: "bg-orange-500/10", border: "border-orange-500/20", position: 4 },
      { id: "Enrolled & Fee Paid", name: "Enrolled & Fee Paid", color: "text-emerald-500", bg: "bg-emerald-500/10", border: "border-emerald-500/20", position: 5, isWon: true },
      { id: "Dropped / Not Interested", name: "Dropped / Not Interested", color: "text-slate-400", bg: "bg-slate-500/10", border: "border-slate-500/20", position: 6, isLost: true },
    ],
  },

  custom: {
    name: "Custom / General B2B",
    icon: "⚡",
    description: "Fully open canvas with customizable fields, labels, and pipeline stages.",
    fields: [
      { key: "name", label: "Contact / Lead Name", type: "text", visible: true, required: true, placeholder: "e.g. Lead Contact" },
      { key: "company_name", label: "Organization / Company", type: "text", visible: true, required: true, placeholder: "e.g. Organization Name" },
      { key: "phone", label: "Phone / WhatsApp", type: "text", visible: true, required: true, placeholder: "+91 98765 43210" },
      { key: "email", label: "Email Address", type: "text", visible: true, required: false, placeholder: "contact@company.com" },
      { key: "service_type", label: "Product / Service", type: "text", visible: true, required: true, placeholder: "Item or Service" },
      { key: "estimated_deal_value", label: "Deal Value", type: "number", visible: true, required: true, placeholder: "50000" },
      { key: "country_or_city", label: "Location", type: "text", visible: true, required: false, placeholder: "City" },
      { key: "priority", label: "Priority", type: "select", visible: true, required: true, placeholder: "HOT" },
      { key: "notes", label: "Notes", type: "textarea", visible: true, required: false, placeholder: "General notes" },
    ],
    pipelineStages: [
      { id: "New Lead", name: "New Lead", color: "text-blue-500", bg: "bg-blue-500/10", border: "border-blue-500/20", position: 1 },
      { id: "Discovery Call", name: "Discovery Call", color: "text-violet-500", bg: "bg-violet-500/10", border: "border-violet-500/20", position: 2 },
      { id: "Proposal Sent", name: "Proposal Sent", color: "text-amber-500", bg: "bg-amber-500/10", border: "border-amber-500/20", position: 3 },
      { id: "Negotiation", name: "Negotiation", color: "text-orange-500", bg: "bg-orange-500/10", border: "border-orange-500/20", position: 4 },
      { id: "Closed Won", name: "Closed Won", color: "text-emerald-500", bg: "bg-emerald-500/10", border: "border-emerald-500/20", position: 5, isWon: true },
      { id: "Closed Lost", name: "Closed Lost", color: "text-slate-400", bg: "bg-slate-500/10", border: "border-slate-500/20", position: 6, isLost: true },
    ],
  },
};

/**
 * Returns the default workspace configuration for a user.
 * Intelligently defaults based on user industry or company name (e.g. 'John Traders' -> 'trading').
 */
export function getDefaultWorkspaceConfig(userId: number, companyName?: string, userIndustry?: string): WorkspaceConfig {
  let profile = "services";
  const nameLower = (companyName || "").toLowerCase();
  const indLower = (userIndustry || "").toLowerCase();

  if (indLower.includes("trade") || indLower.includes("commodity") || indLower.includes("export") || indLower.includes("import") || indLower.includes("wholesale") || nameLower.includes("trade") || nameLower.includes("trader") || nameLower.includes("commodity") || nameLower.includes("export") || nameLower.includes("import") || nameLower.includes("wholesale")) {
    profile = "trading";
  } else if (indLower.includes("real") || indLower.includes("estate") || nameLower.includes("real") || nameLower.includes("estate") || nameLower.includes("realty") || nameLower.includes("properties") || nameLower.includes("broker")) {
    profile = "real_estate";
  } else if (indLower.includes("health") || indLower.includes("clinic") || indLower.includes("dental") || nameLower.includes("clinic") || nameLower.includes("dental") || nameLower.includes("hospital") || nameLower.includes("doctor")) {
    profile = "healthcare";
  } else if (indLower.includes("manuf") || indLower.includes("industrial") || nameLower.includes("industry") || nameLower.includes("manufacturing") || nameLower.includes("motors") || nameLower.includes("fabricat")) {
    profile = "manufacturing";
  } else if (indLower.includes("logistic") || indLower.includes("freight") || indLower.includes("warehouse") || indLower.includes("cargo") || nameLower.includes("logistic") || nameLower.includes("freight") || nameLower.includes("cargo") || nameLower.includes("shipping")) {
    profile = "logistics";
  } else if (indLower.includes("gym") || indLower.includes("fitness") || indLower.includes("crossfit") || nameLower.includes("gym") || nameLower.includes("fitness")) {
    profile = "fitness";
  } else if (indLower.includes("restaurant") || indLower.includes("hospitality") || indLower.includes("banquet") || indLower.includes("catering") || nameLower.includes("restaurant") || nameLower.includes("cafe") || nameLower.includes("kitchen")) {
    profile = "hospitality";
  } else if (indLower.includes("legal") || indLower.includes("law") || indLower.includes("attorney") || indLower.includes("ca") || nameLower.includes("law") || nameLower.includes("legal") || nameLower.includes("advocate")) {
    profile = "legal";
  } else if (indLower.includes("edu") || indLower.includes("school") || indLower.includes("college") || indLower.includes("institute") || indLower.includes("coaching") || nameLower.includes("academy") || nameLower.includes("institute")) {
    profile = "education";
  } else if (indLower.includes("custom") || indLower.includes("general")) {
    profile = "custom";
  }

  const preset = WORKSPACE_PRESETS[profile] || WORKSPACE_PRESETS.services;

  return {
    userId,
    businessProfile: profile,
    fields: JSON.parse(JSON.stringify(preset.fields)),
    pipelineStages: JSON.parse(JSON.stringify(preset.pipelineStages)),
    importMappings: {},
  };
}

/**
 * Generates dynamic sample CSV template content and filename tailored strictly to a workspace configuration.
 * Uses visible field display labels and active pipeline stage names.
 */
export function generateWorkspaceSampleCsv(
  config?: WorkspaceConfig | null,
  companyName?: string | null
): { filename: string; csvContent: string; profileName: string; visibleLabels: string[] } {
  const profileKey = config?.businessProfile || "trading";
  const preset = WORKSPACE_PRESETS[profileKey] || WORKSPACE_PRESETS.trading;
  const profileName = preset?.name || "Business";

  // Visible fields configured in Fields & Table Headers
  const visibleFields = (config?.fields && config?.fields.length > 0
    ? config.fields.filter((f) => f.visible !== false)
    : preset.fields
  );

  // Derive column headers
  const headers = visibleFields.map((f) => f.label);
  
  // Ensure "Pipeline Stage" and "Currency" are represented in template if not explicitly a field
  const hasStageHeader = visibleFields.some(
    (f) => f.key === "status" || f.key === "stage" || f.label.toLowerCase().includes("stage") || f.label.toLowerCase().includes("status")
  );
  if (!hasStageHeader) {
    headers.push("Pipeline Stage");
  }

  const hasCurrencyHeader = visibleFields.some(
    (f) => f.key === "currency" || f.label.toLowerCase().includes("currency")
  );
  if (!hasCurrencyHeader) {
    headers.push("Currency");
  }

  // Active pipeline stage names
  const stageNames = (config?.pipelineStages && config?.pipelineStages.length > 0
    ? config.pipelineStages.map((s) => s.name)
    : preset.pipelineStages.map((s) => s.name)
  );

  const stage1 = stageNames[0] || "Inquiry / RFQ";
  const stage2 = stageNames[1] || stageNames[0] || "Quotation Sent";
  const stage3 = stageNames[2] || stageNames[1] || "Sample / Specs";

  // Realistic sample row generator based on field key and profile
  const sampleRowValues: Record<string, Record<string, string[]>> = {
    trading: {
      name: ["Rajesh Singhania", "Abdullah Al-Zaabi", "Vikram Malhotra"],
      company_name: ["Singhania Metals & Tubes Pvt Ltd", "Emirates Agro Trade LLC", "Malhotra Polymers Global"],
      phone: ["+91 98765 43210", "+971 50 123 4567", "+91 98200 11223"],
      email: ["purchase@singhaniametals.com", "import@emiratesagro.ae", "vmalhotra@polymersglobal.in"],
      service_type: ["Hot Rolled Steel Coils", "Basmati Rice Grade 1121", "Polypropylene Injection Granules"],
      quantity: ["500", "2000", "120"],
      unit: ["Metric Tonnes (MT)", "Bags (50kg)", "MT"],
      estimated_deal_value: ["7500000", "1850000", "1450000"],
      country_or_city: ["Nhava Sheva Port, Mumbai", "Jebel Ali Port, Dubai", "Kandla Port, Gujarat"],
      priority: ["HOT", "HOT", "WARM"],
      notes: [
        "100% LC at sight, CIF Nhava Sheva, SGS test certificate required",
        "30% Advance deposit, 70% against BL copy",
        "FOB Kandla, Letter of Credit terms under review",
      ],
    },
    services: {
      name: ["John Miller", "Aarav Sharma", "Priya Nair"],
      company_name: ["Acme Global Corp", "TechNova Systems", "Nair Digital Group"],
      phone: ["+1 415 555 2671", "+91 98765 43210", "+91 98200 11223"],
      email: ["john@acme.com", "aarav@technova.com", "priya@nairdigital.com"],
      service_type: ["Cloud ERP & Automation Suite", "Custom Next.js SaaS Web Application", "AI WhatsApp Agent Integration"],
      estimated_deal_value: ["650000", "450000", "280000"],
      country_or_city: ["San Francisco", "Bangalore", "Mumbai"],
      priority: ["HOT", "WARM", "HOT"],
      notes: [
        "Target rollout in Q4, SOC2 compliance required",
        "Phase 1 MVP in 6 weeks with automated onboarding",
        "Multi-agent workflow with CRM sync",
      ],
    },
    real_estate: {
      name: ["Vikram Malhotra", "Kavita Singhal", "Farhan Akhtar"],
      company_name: ["Malhotra Holdings", "Singhal Family Office", "Prime Investments"],
      phone: ["+971 50 123 4567", "+91 98765 43210", "+91 98200 11223"],
      email: ["vikram@example.com", "kavita@singhal.com", "farhan@primeinvest.ae"],
      service_type: ["3BHK Luxury Waterfront Apartment", "Commercial Retail Showroom", "Golf Course Penthouse"],
      estimated_deal_value: ["18500000", "35000000", "24000000"],
      country_or_city: ["Dubai Marina", "Indiranagar, Bangalore", "Worli, Mumbai"],
      priority: ["HOT", "HOT", "WARM"],
      notes: [
        "Pre-approved mortgage, site inspection this Saturday",
        "High rental yield investment requirement",
        "Ready to move, token advance ready",
      ],
    },
    manufacturing: {
      name: ["Suresh Patel", "Anand Kulkarni", "David Smith"],
      company_name: ["Patel Precision Gears", "Kulkarni Heavy Castings", "Smith Motor Components"],
      phone: ["+91 98765 43210", "+91 98200 11223", "+1 312 555 7890"],
      email: ["spatel@precisiongears.in", "anand@kulkarnicastings.com", "dsmith@smithmotors.com"],
      service_type: ["Helical Transmission Gears", "Ductile Iron Pump Casings", "CNC Turned Steel Shafts"],
      quantity: ["5000", "250", "1200"],
      unit: ["Pieces", "Units", "Sets"],
      estimated_deal_value: ["3800000", "1650000", "5200000"],
      country_or_city: ["Pune Industrial Area", "Peenya, Bangalore", "Detroit, USA"],
      priority: ["HOT", "WARM", "HOT"],
      notes: [
        "PPAP Level 3 documentation required, batch delivery by end of month",
        "EN-GJS-400-15 grade material with ultrasonic inspection",
        "Precision tolerance ±0.005mm, CMM inspection report needed",
      ],
    },
    healthcare: {
      name: ["Dr. Sunita Rao", "Dr. Rajesh Varma", "Anita Nair"],
      company_name: ["Apollo Dental Clinic", "Varma Specialty Hospital", "CureWell Diagnostics"],
      phone: ["+91 98765 43210", "+91 98200 11223", "+91 98800 33445"],
      email: ["dr.sunita@apollodental.in", "director@varmahospitals.com", "anita@curewell.org"],
      service_type: ["Dental Implant & Smile Design Package", "Executive Master Health Checkup", "Orthopedic Consultation & MRI"],
      estimated_deal_value: ["85000", "25000", "42000"],
      country_or_city: ["Indiranagar, Bangalore", "Bandra, Mumbai", "Jayanagar, Bangalore"],
      priority: ["HOT", "HOT", "WARM"],
      notes: [
        "Appointment slot requested for Saturday morning",
        "Family health package including cardiac screening",
        "Follow-up consultation after MRI scans",
      ],
    },
    logistics: {
      name: ["Capt. Ramesh Menon", "Fahad Al-Qasimi", "Deepak Joshi"],
      company_name: ["OceanBridge Freightways", "Gulf Horizon Shipping", "SpeedCargo Express"],
      phone: ["+91 98765 43210", "+971 50 123 4567", "+91 98200 11223"],
      email: ["rmenon@oceanbridge.com", "operations@gulfhorizon.ae", "deepak@speedcargo.in"],
      service_type: ["FCL Ocean Freight (40ft High Cube)", "Air Cargo Express Shipment", "Cross-Border Customs Clearance"],
      quantity: ["10", "450", "4"],
      unit: ["Containers (FEU)", "Kilograms (KG)", "Truckloads"],
      estimated_deal_value: ["850000", "320000", "540000"],
      country_or_city: ["Nhava Sheva to Jebel Ali", "Singapore to Bangalore", "Mundra to Rotterdam"],
      priority: ["HOT", "HOT", "WARM"],
      notes: [
        "Temperature-controlled reefer cargo with phytosanitary certificate",
        "Express clearance at Bangalore Airport, hazardous cargo declaration ready",
        "Customs bonded warehousing required for 14 days",
      ],
    },
  };

  const currentProfileSamples = sampleRowValues[profileKey] || sampleRowValues.trading;
  const stagesToUse = [stage1, stage2, stage3];
  const currenciesToUse = ["INR", profileKey === "real_estate" ? "AED" : "USD", "INR"];

  const escapeVal = (val: any) => {
    if (val === null || val === undefined) return '""';
    const s = String(val);
    return `"${s.replace(/"/g, '""')}"`;
  };

  const rows: string[][] = [0, 1, 2].map((idx) => {
    const rowVals = visibleFields.map((f) => {
      // 1. If sample mapping exists for this key
      if (currentProfileSamples[f.key] && currentProfileSamples[f.key][idx]) {
        return currentProfileSamples[f.key][idx];
      }
      // 2. Fallbacks based on placeholder or label
      if (f.placeholder && !f.placeholder.startsWith("e.g.")) {
        return f.placeholder;
      }
      if (f.placeholder && f.placeholder.startsWith("e.g.")) {
        return f.placeholder.replace(/^e\.g\.\s*/, "");
      }
      if (f.type === "number") return "100";
      return `Sample ${f.label}`;
    });

    if (!hasStageHeader) {
      rowVals.push(stagesToUse[idx]);
    }
    if (!hasCurrencyHeader) {
      rowVals.push(currenciesToUse[idx]);
    }

    return rowVals;
  });

  const csvContent = "\uFEFF" + [
    headers.map(escapeVal).join(","),
    ...rows.map((r) => r.map(escapeVal).join(",")),
  ].join("\r\n");

  const cleanName = (companyName || profileKey).toLowerCase().replace(/[^a-z0-9]/g, "_").slice(0, 24);
  const filename = `${cleanName}_leads_template.csv`;

  return {
    filename,
    csvContent,
    profileName,
    visibleLabels: headers,
  };
}
